import { Button, Filter, IconButton } from '@vrplatform/components';
import { useApiQuery, useTeam } from '@vrplatform/data';
import {
  MantineReactTable,
  useLazyTable,
  type MRT_ColumnDef,
} from '@vrplatform/table';
import {
  type Maybe,
  day,
  hasValue,
  isUUID,
  sortBy,
  toTitleCase,
} from '@vrplatform/utils';
import { Box, Group, Modal } from '@mantine/core';
import { Text } from '@mantine/core';
import {
  readLocalStorageValue,
  useDisclosure,
  useSetState,
} from '@mantine/hooks';
import { formatPaymentLineType, pluralize } from '@vrplatform/ui-common';
import { useMemo } from 'react';
import {
  TableStoreProvider,
  useTableStoreGetSelectionState,
  useTableStoreSelecting,
} from '../../../stores';
import { useSalesTaxLiabilityTableFilter } from './useSalesTaxLiabilityTableFilter';
import { CustomFilter } from '../../../components';

export const SalesTaxLiabilityTableFilter = () => {
  return (
    <Group flex={1}>
      <Date />
      <TaxableLineIds />
      <BookingChannel />
    </Group>
  );
};

const Date = () => {
  const [{ effectiveStatementStartAt: statementStartAt }] = useTeam();

  const { filter, setDate } = useSalesTaxLiabilityTableFilter();

  return (
    <Filter.Date
      value={filter.date}
      clearable={false}
      minDate={statementStartAt ? day(statementStartAt).toDate() : undefined}
      setValue={(value) => {
        setDate(
          value
            ?.filter(hasValue)
            .map((date) => day(date).yyyymmdd())
            .join('...') || null
        );
      }}
    />
  );
};

const BookingChannel = () => {
  const { filter, setChannels } = useSalesTaxLiabilityTableFilter();

  return (
    <CustomFilter.BookingChannel
      bookingChannels={filter.channels}
      setBookingChannels={(v) => setChannels(v?.length ? v.join(',') : null)}
    />
  );
};

const TaxableLineIds = () => {
  const { filter, setTaxableLineIds, localStorageKey } =
    useSalesTaxLiabilityTableFilter();

  const [opened, handlers] = useDisclosure();

  const onSubmit = (taxableLineIds: string[]) => {
    setTaxableLineIds(taxableLineIds);
    handlers.close();
  };

  const selected = useMemo(
    () =>
      readLocalStorageValue<string[]>({
        key: localStorageKey,
      })?.reduce((arr: Record<string, boolean>, id) => {
        if (!id) return arr;
        arr[id] = true;
        return arr;
      }, {}),
    [localStorageKey]
  );

  const isDefault = !filter.taxableLineIds.length;

  return (
    <>
      <Filter.Pill
        isDefault={isDefault}
        label={
          isDefault
            ? 'Reservation Financials'
            : `${filter.taxableLineIds.length} ${pluralize(filter.taxableLineIds.length, 'financial')} selected`
        }
        opened={opened}
        onClick={handlers.open}
        resetFilter={() => setTaxableLineIds([])}
      />
      <TableStoreProvider
        key={localStorageKey}
        filterType="state"
        selected={selected}
      >
        <TaxableLineIdsModal
          opened={opened}
          closeModal={handlers.close}
          onSubmit={onSubmit}
        />
      </TableStoreProvider>
    </>
  );
};

function useReservationLineMappingsTableQuery(filter: {
  search: Maybe<string>;
  appId: Maybe<string>;
  accountId: Maybe<string>;
  opened: boolean;
}) {
  const [{ id: teamId }] = useTeam();

  return useApiQuery(
    async ($api, signal, args) => {
      const result = await $api.GET('/reservations/line-mappings', {
        params: {
          query: {
            limit: 100,
            appId: args.appId,
            accountId:
              args.accountId && isUUID(args.accountId)
                ? args.accountId
                : undefined,
            status:
              args.accountId === 'excluded'
                ? 'excluded'
                : args.accountId === 'unmapped'
                  ? 'unmapped'
                  : undefined,
          },
        },
        signal,
      });

      if (result.error) throw result.error;

      const rows = result.data.data.map((x) => ({
        ...x,
        accountId: x.account?.id || 'unmapped',
        sortKey:
          x.status === 'mapped'
            ? `0_${x.account?.name || '-'}_${x.type}`
            : x.status === 'excluded'
              ? `1_${x.type}`
              : `2_${x.type}`,
      }));

      return sortBy(rows, 'sortKey');
    },
    {
      queryGroup: 'reports',
      uniqueQueryKey: 'sales-tax-liability-line-mappings',
      skip: !teamId || !filter.opened,
      variables: {
        search: filter.search?.trim(),
        appId: filter.appId ?? undefined,
        accountId: filter.accountId as Maybe<
          'unmapped' | 'excluded' | (string & {})
        >,
      },
    }
  );
}

type TableQueryData = NonNullable<
  ReturnType<typeof useReservationLineMappingsTableQuery>['data']
>;
type ReservationLineMappingsTableRow = TableQueryData[0];

const TaxableLineIdsModal = ({
  closeModal,
  onSubmit,
  opened,
}: {
  opened: boolean;
  closeModal: () => void;
  onSubmit: (taxableLineIds: string[]) => void;
}) => {
  const [filter, setFilter] = useSetState<{
    search: string;
    accountId: string | null;
  }>({
    search: '',
    accountId: null,
  });

  const selecting = useTableStoreSelecting<ReservationLineMappingsTableRow>({
    disableSelectGroups: false,
  });
  const getSelected = useTableStoreGetSelectionState();

  const queryData = useReservationLineMappingsTableQuery({
    search: filter.search,
    appId: undefined,
    accountId: filter.accountId,
    opened,
  });

  const columns = useMemo<MRT_ColumnDef<ReservationLineMappingsTableRow>[]>(
    () => [
      {
        header: 'Account',
        accessorKey: 'accountId',
        enableGrouping: true,
      },
      {
        header: 'PMS Line',
        accessorKey: 'type',
        AggregatedCell: ({ row }) => {
          const firstSubRow = row.subRows?.at(0)?.original;

          if (
            firstSubRow?.status === 'unmapped' ||
            firstSubRow?.status === 'excluded'
          )
            return <Text fw={500}>{toTitleCase(firstSubRow?.status)}</Text>;

          return (
            <Box>
              <Text size="xs" c="gray">
                Account
              </Text>
              <Text>{row.original.account?.name}</Text>
            </Box>
          );
        },
        Cell: ({ row }) => {
          const { app, line } = formatPaymentLineType(row.original);

          return (
            <Box>
              {app && (
                <Text c="gray" size="xs">
                  {app}
                </Text>
              )}
              <Text>{line}</Text>
            </Box>
          );
        },
      },
    ],
    []
  );

  const tableProps = useLazyTable({
    data: {
      rows: queryData.data ?? [],
      rowCount: queryData.data?.length ?? 0,
      loading: queryData.isLoading,
      error: queryData.error?.message,
    },
    table: {
      columns,
      hidePagination: true,
      hideTopBar: true,
      hideHeader: true,
      onRowClick: {
        handler: (row) => {
          selecting?.setRowSelection({
            ...selecting.rowSelection.rows,
            [row.id]: !selecting.rowSelection.rows[row.id],
          });
        },
      },
    },
    selecting,
    groupBy: {
      groupBy: ['accountId'],
      setGroupBy: () => {},
      defaultExpanded: true,
      allowExpanding: true,
    },
    styles: {
      row: (row, theme) => {
        if (!row.row.subRows?.length) return {};

        return {
          backgroundColor: `${theme.colors.neutral[1]}!important`,
        };
      },
    },
  });

  return (
    <Modal
      centered
      opened={opened}
      onClose={closeModal}
      size={800}
      title="Select taxable reservation financials"
      styles={{
        title: {
          fontWeight: 600,
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 0,
          paddingTop: 2,
          // height: '85dvh',
          minHeight: 600,
        },
      }}
    >
      <Text mb="sm" c="gray">
        Select your reservation financials to calculate the taxable amount on
        each reservation
      </Text>

      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          height: 500,
          border: `1px solid ${theme.colors.gray[2]}`,
          borderRadius: theme.radius.md,
          overflow: 'hidden',
        })}
      >
        <Group
          gap="xs"
          sx={(theme) => ({
            borderBottom: `1px solid ${theme.colors.gray[2]}`,
            padding: theme.spacing.xs,
          })}
          wrap="nowrap"
        >
          <IconButton
            icon="ChevronIcon"
            onClick={() => tableProps.toggleAllRowsExpanded()}
            sx={{
              transform: `rotate(${tableProps.getIsSomeRowsExpanded() ? 0 : -90}deg)`,
              transition: 'transform 100ms ease',
            }}
            iconSize={20}
            color={(theme) => theme.colors.neutral[5]}
          />
          <Group gap="xs">
            <Filter.Search
              value={filter.search}
              setValue={(v) => setFilter({ search: v })}
            />
            <CustomFilter.Account
              account={filter.accountId}
              setAccount={(accountId) => setFilter({ accountId })}
              type="single"
            />
          </Group>
        </Group>

        <MantineReactTable table={tableProps} />
      </Box>
      <Group py="md" justify="end">
        <Button onClick={closeModal}>Cancel</Button>
        <Button
          type="submit"
          variant="primary"
          onClick={() => {
            const ids = getSelected().selected;

            onSubmit(
              Object.entries(ids)
                .filter(([k, v]) => v && isUUID(k))
                .map(([k]) => k)
            );
          }}
        >
          Select financials
        </Button>
      </Group>
    </Modal>
  );
};
